/**
 * Utilities for converting Mastra Spans to OTel Spans
 * with Semantic conventions for generative AI systems
 * @see https://github.com/open-telemetry/semantic-conventions/blob/v1.38.0/docs/gen-ai/README.md
 * @see https://github.com/open-telemetry/semantic-conventions/blob/v1.38.0/docs/gen-ai/gen-ai-events.md
 * @see https://github.com/open-telemetry/semantic-conventions/blob/v1.38.0/docs/gen-ai/gen-ai-spans.md
 * @see https://github.com/open-telemetry/semantic-conventions/blob/v1.38.0/docs/gen-ai/gen-ai-agent-spans.md
 * @see https://opentelemetry.io/docs/specs/semconv/gen-ai/non-normative/examples-llm-calls/
 * @see https://opentelemetry.io/docs/specs/semconv/registry/attributes/gen-ai/
 */

import { SpanType } from '@mastra/core/observability';
import type {
  AgentRunAttributes,
  AnyExportedSpan,
  MCPServerRequestAttributes,
  MCPToolCallAttributes,
  ModelGenerationAttributes,
  ModelInferenceAttributes,
  ModelStepAttributes,
  RagEmbeddingAttributes,
  ToolCallAttributes,
  UsageStats,
  WorkflowConditionalAttributes,
  WorkflowConditionalEvalAttributes,
  WorkflowLoopAttributes,
  WorkflowParallelAttributes,
  WorkflowRunAttributes,
  WorkflowSleepAttributes,
  WorkflowStepAttributes,
  WorkflowWaitEventAttributes,
} from '@mastra/core/observability';
import type { Attributes } from '@opentelemetry/api';
import {
  ATTR_ERROR_MESSAGE,
  ATTR_ERROR_TYPE,
  ATTR_GEN_AI_PROVIDER_NAME,
  ATTR_GEN_AI_REQUEST_MODEL,
  ATTR_GEN_AI_RESPONSE_MODEL,
  ATTR_GEN_AI_REQUEST_MAX_TOKENS,
  ATTR_GEN_AI_REQUEST_TEMPERATURE,
  ATTR_GEN_AI_REQUEST_TOP_P,
  ATTR_GEN_AI_REQUEST_TOP_K,
  ATTR_GEN_AI_REQUEST_PRESENCE_PENALTY,
  ATTR_GEN_AI_REQUEST_FREQUENCY_PENALTY,
  ATTR_GEN_AI_REQUEST_STOP_SEQUENCES,
  ATTR_GEN_AI_REQUEST_SEED,
  ATTR_GEN_AI_INPUT_MESSAGES,
  ATTR_GEN_AI_OUTPUT_MESSAGES,
  ATTR_GEN_AI_USAGE_INPUT_TOKENS,
  ATTR_GEN_AI_USAGE_OUTPUT_TOKENS,
  ATTR_GEN_AI_USAGE_CACHE_READ_INPUT_TOKENS,
  ATTR_GEN_AI_USAGE_CACHE_CREATION_INPUT_TOKENS,
  ATTR_GEN_AI_AGENT_ID,
  ATTR_GEN_AI_AGENT_NAME,
  ATTR_GEN_AI_TOOL_DESCRIPTION,
  ATTR_GEN_AI_OPERATION_NAME,
  ATTR_GEN_AI_RESPONSE_FINISH_REASONS,
  ATTR_GEN_AI_RESPONSE_ID,
  ATTR_GEN_AI_CONVERSATION_ID,
  ATTR_GEN_AI_SYSTEM_INSTRUCTIONS,
  ATTR_SERVER_ADDRESS,
  ATTR_SERVER_PORT,
  ATTR_GEN_AI_TOOL_NAME,
} from '@opentelemetry/semantic-conventions/incubating';
import { isModelInferenceEnabled } from './features';
import { convertMastraMessagesToGenAIMessages } from './gen-ai-messages';

/**
 * Token usage attributes following OTel GenAI semantic conventions.
 * @see https://opentelemetry.io/docs/specs/semconv/gen-ai/gen-ai-spans/
 */
export interface OtelUsageMetrics {
  [ATTR_GEN_AI_USAGE_INPUT_TOKENS]?: number;
  [ATTR_GEN_AI_USAGE_OUTPUT_TOKENS]?: number;
  [ATTR_GEN_AI_USAGE_CACHE_READ_INPUT_TOKENS]?: number;
  [ATTR_GEN_AI_USAGE_CACHE_CREATION_INPUT_TOKENS]?: number;
  'gen_ai.usage.cache_creation.5m_input_tokens'?: number;
  'gen_ai.usage.cache_creation.1h_input_tokens'?: number;
  'gen_ai.usage.reasoning_tokens'?: number;
  'gen_ai.usage.audio_input_tokens'?: number;
  'gen_ai.usage.audio_output_tokens'?: number;
}

/**
 * Formats UsageStats to OTel GenAI semantic convention attributes.
 */
export function formatUsageMetrics(usage?: UsageStats): OtelUsageMetrics {
  if (!usage) return {};

  const metrics: OtelUsageMetrics = {};

  if (usage.inputTokens !== undefined) {
    metrics[ATTR_GEN_AI_USAGE_INPUT_TOKENS] = usage.inputTokens;
  }

  if (usage.outputTokens !== undefined) {
    metrics[ATTR_GEN_AI_USAGE_OUTPUT_TOKENS] = usage.outputTokens;
  }

  // Reasoning tokens from outputDetails
  if (usage.outputDetails?.reasoning !== undefined) {
    metrics['gen_ai.usage.reasoning_tokens'] = usage.outputDetails.reasoning;
  }

  // Cache read input tokens (subset of input_tokens)
  if (usage.inputDetails?.cacheRead !== undefined) {
    metrics[ATTR_GEN_AI_USAGE_CACHE_READ_INPUT_TOKENS] = usage.inputDetails.cacheRead;
  }

  // Cache creation input tokens (subset of input_tokens)
  if (usage.inputDetails?.cacheWrite !== undefined) {
    metrics[ATTR_GEN_AI_USAGE_CACHE_CREATION_INPUT_TOKENS] = usage.inputDetails.cacheWrite;
  }
  if (usage.inputDetails?.cacheWrite5m !== undefined) {
    metrics['gen_ai.usage.cache_creation.5m_input_tokens'] = usage.inputDetails.cacheWrite5m;
  }
  if (usage.inputDetails?.cacheWrite1h !== undefined) {
    metrics['gen_ai.usage.cache_creation.1h_input_tokens'] = usage.inputDetails.cacheWrite1h;
  }

  // Audio tokens from inputDetails/outputDetails
  if (usage.inputDetails?.audio !== undefined) {
    metrics['gen_ai.usage.audio_input_tokens'] = usage.inputDetails.audio;
  }
  if (usage.outputDetails?.audio !== undefined) {
    metrics['gen_ai.usage.audio_output_tokens'] = usage.outputDetails.audio;
  }

  return metrics;
}

function addModelRequestAttributes(
  attributes: Attributes,
  attrs: Pick<ModelGenerationAttributes | RagEmbeddingAttributes, 'model' | 'provider' | 'usage'>,
): void {
  if (attrs.model) {
    attributes[ATTR_GEN_AI_REQUEST_MODEL] = attrs.model;
  }

  if (attrs.provider) {
    attributes[ATTR_GEN_AI_PROVIDER_NAME] = normalizeProvider(attrs.provider);
  }

  Object.assign(attributes, formatUsageMetrics(attrs.usage));
}

/** Attributes of whichever span represents the model call. */
type ModelCallAttributes = ModelGenerationAttributes & ModelInferenceAttributes;

/**
 * Whether this span is exported as the GenAI inference (`chat`) span.
 *
 * Exactly one span per model call carries `gen_ai.request.model`, the messages
 * and `gen_ai.usage.*`, because OTel backends (Langfuse, Phoenix, ...) sum usage
 * across nested spans. With paired packages that emit MODEL_INFERENCE, that is
 * the call; MODEL_GENERATION is then the parent loop and MODEL_STEP one turn of
 * it. Older pairings only emit MODEL_GENERATION, which keeps the `chat` role.
 */
export function isModelCallSpan(type: SpanType): boolean {
  return type === (isModelInferenceEnabled() ? SpanType.MODEL_INFERENCE : SpanType.MODEL_GENERATION);
}

export interface GenAISemanticsOptions {
  /**
   * Override {@link isModelCallSpan} for this span. Exporters that flatten the
   * generation loop into a single `chat` span pass `true` for MODEL_GENERATION.
   */
  modelCall?: boolean;
}

/**
 * Get the operation name based on span type for gen_ai.operation.name
 */
function getOperationName(span: AnyExportedSpan, modelCall = isModelCallSpan(span.type)): string {
  if (modelCall) {
    return 'chat';
  }
  switch (span.type) {
    case SpanType.MODEL_STEP:
      return 'agent_step';
    case SpanType.RAG_EMBEDDING:
      return 'embeddings';
    case SpanType.TOOL_CALL:
    case SpanType.MCP_TOOL_CALL:
    case SpanType.PROVIDER_TOOL_CALL:
      return 'execute_tool';
    case SpanType.AGENT_RUN:
      return 'invoke_agent';
    case SpanType.WORKFLOW_RUN:
      return 'invoke_workflow';
    default:
      return span.type.toLowerCase();
  }
}
/**
 * Keep only unicode letters, numbers, dot, underscore, space, dash.
 */
function sanitizeSpanName(name: string): string {
  return name.replace(/[^\p{L}\p{N}._ -]/gu, '');
}

function getSpanIdentifier(span: AnyExportedSpan): string | undefined {
  switch (span.type) {
    case SpanType.MODEL_GENERATION:
    case SpanType.MODEL_INFERENCE: {
      const attrs = span.attributes as ModelCallAttributes | undefined;
      return attrs?.model;
    }
    case SpanType.RAG_EMBEDDING: {
      const attrs = span.attributes as RagEmbeddingAttributes;
      return attrs?.model;
    }

    // Workflow step spans set their own entityId (the step id) but inherit
    // entityName from the enclosing workflow, so identify them by entityId.
    case SpanType.WORKFLOW_STEP:
      return span.entityId;

    // Control-flow spans set no entity of their own and would otherwise inherit
    // the enclosing workflow's entityName, collapsing siblings onto one name.
    // Fall through to the authored span name, which already carries the
    // condition index / branch descriptor.
    case SpanType.WORKFLOW_CONDITIONAL:
    case SpanType.WORKFLOW_CONDITIONAL_EVAL:
    case SpanType.WORKFLOW_PARALLEL:
    case SpanType.WORKFLOW_LOOP:
    case SpanType.WORKFLOW_SLEEP:
    case SpanType.WORKFLOW_WAIT_EVENT:
      return undefined;

    default:
      return span.entityName ?? span.entityId;
  }
}

/**
 * Get an OTEL-compliant span name based on span type and attributes
 */
export function getSpanName(span: AnyExportedSpan, options?: GenAISemanticsOptions): string {
  const identifier = getSpanIdentifier(span);

  if (identifier) {
    const operation = getOperationName(span, options?.modelCall);
    return `${operation} ${identifier}`;
  }

  // For other types, use a simplified version of the original name
  return sanitizeSpanName(span.name);
}

/**
 * Adds authored workflow entry identity (id, description, metadata) for
 * control-flow spans. Metadata is JSON-serialized to keep nested values and
 * falsy members (false, 0) intact; absent fields add no key.
 */
function addEntryAttributes(
  attributes: Attributes,
  spanType: string,
  attrs: { entryId?: string; entryDescription?: string; entryMetadata?: Record<string, unknown> },
): void {
  if (attrs.entryId !== undefined) {
    attributes[`mastra.${spanType}.entry_id`] = attrs.entryId;
  }
  if (attrs.entryDescription !== undefined) {
    attributes[`mastra.${spanType}.entry_description`] = attrs.entryDescription;
  }
  if (attrs.entryMetadata !== undefined) {
    attributes[`mastra.${spanType}.entry_metadata`] = JSON.stringify(attrs.entryMetadata);
  }
}

/**
 * Gets OpenTelemetry attributes from Mastra Span
 * Following OTEL Semantic Conventions for GenAI
 */
export function getAttributes(span: AnyExportedSpan, options?: GenAISemanticsOptions): Attributes {
  const attributes: Attributes = {};
  const spanType = span.type.toLowerCase();
  const modelCall = options?.modelCall ?? isModelCallSpan(span.type);

  // Add gen_ai.operation.name based on span type
  attributes[ATTR_GEN_AI_OPERATION_NAME] = getOperationName(span, modelCall);

  // Add span type for better visibility
  attributes['mastra.span.type'] = span.type;

  // Handle input/output based on span type
  // Always add input/output for Laminar compatibility
  if (span.input !== undefined) {
    const inputStr = typeof span.input === 'string' ? span.input : JSON.stringify(span.input);
    // Add specific attributes based on span type
    if (modelCall) {
      attributes[ATTR_GEN_AI_INPUT_MESSAGES] = convertMastraMessagesToGenAIMessages(inputStr);
    } else if (
      span.type === SpanType.TOOL_CALL ||
      span.type === SpanType.MCP_TOOL_CALL ||
      span.type === SpanType.PROVIDER_TOOL_CALL
    ) {
      attributes['gen_ai.tool.call.arguments'] = inputStr;
    } else {
      attributes[`mastra.${spanType}.input`] = inputStr;
    }
  }

  if (span.output !== undefined) {
    const outputStr = typeof span.output === 'string' ? span.output : JSON.stringify(span.output);
    // Add specific attributes based on span type
    if (modelCall) {
      attributes[ATTR_GEN_AI_OUTPUT_MESSAGES] = convertMastraMessagesToGenAIMessages(outputStr);
      // TODO
      // attributes['gen_ai.output.type'] = image/json/speech/text/<other>
    } else if (
      span.type === SpanType.TOOL_CALL ||
      span.type === SpanType.MCP_TOOL_CALL ||
      span.type === SpanType.PROVIDER_TOOL_CALL
    ) {
      attributes['gen_ai.tool.call.result'] = outputStr;
    } else {
      attributes[`mastra.${spanType}.output`] = outputStr;
    }
  }

  // Add model-specific attributes using OTEL semantic conventions
  if (modelCall && span.attributes) {
    const modelAttrs = span.attributes as ModelCallAttributes;

    addModelRequestAttributes(attributes, modelAttrs);

    // Agent context - allows correlating model generation with the agent that invoked it
    if (span.entityId) {
      attributes[ATTR_GEN_AI_AGENT_ID] = span.entityId;
    }

    if (span.entityName) {
      attributes[ATTR_GEN_AI_AGENT_NAME] = span.entityName;
    }

    // Parameters using OTEL conventions
    if (modelAttrs.parameters) {
      if (modelAttrs.parameters.temperature !== undefined) {
        attributes[ATTR_GEN_AI_REQUEST_TEMPERATURE] = modelAttrs.parameters.temperature;
      }
      if (modelAttrs.parameters.maxOutputTokens !== undefined) {
        attributes[ATTR_GEN_AI_REQUEST_MAX_TOKENS] = modelAttrs.parameters.maxOutputTokens;
      }
      if (modelAttrs.parameters.topP !== undefined) {
        attributes[ATTR_GEN_AI_REQUEST_TOP_P] = modelAttrs.parameters.topP;
      }
      if (modelAttrs.parameters.topK !== undefined) {
        attributes[ATTR_GEN_AI_REQUEST_TOP_K] = modelAttrs.parameters.topK;
      }
      if (modelAttrs.parameters.presencePenalty !== undefined) {
        attributes[ATTR_GEN_AI_REQUEST_PRESENCE_PENALTY] = modelAttrs.parameters.presencePenalty;
      }
      if (modelAttrs.parameters.frequencyPenalty !== undefined) {
        attributes[ATTR_GEN_AI_REQUEST_FREQUENCY_PENALTY] = modelAttrs.parameters.frequencyPenalty;
      }
      if (modelAttrs.parameters.stopSequences) {
        attributes[ATTR_GEN_AI_REQUEST_STOP_SEQUENCES] = JSON.stringify(modelAttrs.parameters.stopSequences);
      }
      if (modelAttrs.parameters.seed) {
        attributes[ATTR_GEN_AI_REQUEST_SEED] = modelAttrs.parameters.seed;
      }
    }

    // Completion start time (TTFT) - used by observability backends for time-to-first-token metrics
    if (modelAttrs.completionStartTime) {
      attributes['mastra.completion_start_time'] = modelAttrs.completionStartTime.toISOString();
    }

    // Response attributes
    if (modelAttrs.finishReason) {
      attributes[ATTR_GEN_AI_RESPONSE_FINISH_REASONS] = JSON.stringify([modelAttrs.finishReason]);
    }
    if (modelAttrs.responseModel) {
      attributes[ATTR_GEN_AI_RESPONSE_MODEL] = modelAttrs.responseModel;
    }
    if (modelAttrs.responseId) {
      attributes[ATTR_GEN_AI_RESPONSE_ID] = modelAttrs.responseId;
    }

    // Server attributes
    if (modelAttrs.serverAddress) {
      attributes[ATTR_SERVER_ADDRESS] = modelAttrs.serverAddress;
    }
    if (modelAttrs.serverPort !== undefined) {
      attributes[ATTR_SERVER_PORT] = modelAttrs.serverPort;
    }
  }

  if (span.type === SpanType.MODEL_STEP && span.attributes) {
    const stepAttrs = span.attributes as ModelStepAttributes;
    if (stepAttrs.stepIndex !== undefined) {
      attributes[`mastra.${spanType}.step_index`] = stepAttrs.stepIndex;
    }
    if (stepAttrs.isContinued !== undefined) {
      attributes[`mastra.${spanType}.is_continued`] = stepAttrs.isContinued;
    }
  }

  if (span.type === SpanType.RAG_EMBEDDING && span.attributes) {
    const embeddingAttrs = span.attributes as RagEmbeddingAttributes;

    addModelRequestAttributes(attributes, embeddingAttrs);

    if (embeddingAttrs.mode) {
      attributes[`mastra.${spanType}.mode`] = embeddingAttrs.mode;
    }
    if (embeddingAttrs.dimensions !== undefined) {
      attributes['gen_ai.embeddings.dimension.count'] = embeddingAttrs.dimensions;
      attributes[`mastra.${spanType}.dimensions`] = embeddingAttrs.dimensions;
    }
    if (embeddingAttrs.inputCount !== undefined) {
      attributes[`mastra.${spanType}.input_count`] = embeddingAttrs.inputCount;
    }
  }

  // Add tool-specific attributes using OTEL conventions
  if (
    span.type === SpanType.TOOL_CALL ||
    span.type === SpanType.MCP_TOOL_CALL ||
    span.type === SpanType.PROVIDER_TOOL_CALL
  ) {
    // Tool identification (entityName/entityId are always set by producers)
    attributes[ATTR_GEN_AI_TOOL_NAME] = span.entityName ?? span.entityId;

    const toolCallId =
      (span.attributes as { toolCallId?: string } | undefined)?.toolCallId ?? span.metadata?.toolCallId;
    if (toolCallId) {
      attributes['gen_ai.tool.call.id'] = toolCallId;
    }

    // Attribute-dependent fields (description, type, MCP server)
    if (span.attributes) {
      const toolAttrs = span.attributes as ToolCallAttributes;
      if (toolAttrs.toolDescription) {
        attributes[ATTR_GEN_AI_TOOL_DESCRIPTION] = toolAttrs.toolDescription;
      }
      if (toolAttrs.toolType) {
        attributes['gen_ai.tool.type'] = toolAttrs.toolType;
      }
      if (span.type === SpanType.MCP_TOOL_CALL) {
        const mcpAttrs = span.attributes as MCPToolCallAttributes;
        if (mcpAttrs.mcpServer) {
          attributes[ATTR_SERVER_ADDRESS] = mcpAttrs.mcpServer;
          attributes[`mastra.${spanType}.server_name`] = mcpAttrs.mcpServer;
        }
        if (mcpAttrs.serverVersion) {
          attributes[`mastra.${spanType}.server_version`] = mcpAttrs.serverVersion;
        }
      }
    }
  }

  // Add agent-specific attributes
  if (span.type === SpanType.AGENT_RUN && span.attributes) {
    const agentAttrs = span.attributes as AgentRunAttributes;
    if (span.entityId) {
      attributes[ATTR_GEN_AI_AGENT_ID] = span.entityId;
    }
    if (span.entityName) {
      attributes[ATTR_GEN_AI_AGENT_NAME] = span.entityName;
    }
    if (agentAttrs.conversationId) {
      attributes[ATTR_GEN_AI_CONVERSATION_ID] = agentAttrs.conversationId;
    }
    if (agentAttrs.maxSteps) {
      attributes[`mastra.${spanType}.max_steps`] = agentAttrs.maxSteps;
    }
    if (agentAttrs.availableTools) {
      attributes[`gen_ai.tool.definitions`] = JSON.stringify(agentAttrs.availableTools);
    }

    //TODO:
    // attributes[ATTR_GEN_AI_AGENT_DESCRIPTION] = agentAttrs.description;
    // attributes[ATTR_GEN_AI_REQUEST_MODEL] = agentAttrs.model.name;

    attributes[ATTR_GEN_AI_SYSTEM_INSTRUCTIONS] = agentAttrs.instructions;
  }

  // Add MCP server request attributes (OTel MCP semantic conventions where they exist)
  if (span.type === SpanType.MCP_SERVER_REQUEST && span.attributes) {
    const requestAttrs = span.attributes as MCPServerRequestAttributes;
    attributes['mcp.method.name'] = requestAttrs.mcpMethod;
    attributes[`mastra.${spanType}.server_name`] = requestAttrs.mcpServer;
    if (requestAttrs.targetName) {
      attributes[`mastra.${spanType}.target_name`] = requestAttrs.targetName;
    }
    if (requestAttrs.serverVersion) {
      attributes[`mastra.${spanType}.server_version`] = requestAttrs.serverVersion;
    }
    if (requestAttrs.mcpProtocolVersion) {
      attributes['mcp.protocol.version'] = requestAttrs.mcpProtocolVersion;
    }
    if (requestAttrs.clientName) {
      attributes[`mastra.${spanType}.client_name`] = requestAttrs.clientName;
    }
    if (requestAttrs.clientVersion) {
      attributes[`mastra.${spanType}.client_version`] = requestAttrs.clientVersion;
    }
  }

  // Add workflow-specific attributes. Control-flow spans carry native branch,
  // loop, sleep and wait metadata that is otherwise dropped on export. Values
  // are emitted under the existing `mastra.<span_type>.<snake_case>` convention;
  // arrays/dates are serialized and false/0 are preserved (guard on !== undefined).
  if (span.type === SpanType.WORKFLOW_RUN && span.attributes) {
    const runAttrs = span.attributes as WorkflowRunAttributes;
    if (runAttrs.status !== undefined) {
      attributes[`mastra.${spanType}.status`] = runAttrs.status;
    }
  }

  if (span.type === SpanType.WORKFLOW_STEP) {
    if (span.entityId) {
      attributes[`mastra.${spanType}.step_id`] = span.entityId;
    }
    if (span.attributes) {
      const stepAttrs = span.attributes as WorkflowStepAttributes;
      if (stepAttrs.status !== undefined) {
        attributes[`mastra.${spanType}.status`] = stepAttrs.status;
      }
      addEntryAttributes(attributes, spanType, stepAttrs);
    }
  }

  if (span.type === SpanType.WORKFLOW_CONDITIONAL && span.attributes) {
    const condAttrs = span.attributes as WorkflowConditionalAttributes;
    if (condAttrs.conditionCount !== undefined) {
      attributes[`mastra.${spanType}.condition_count`] = condAttrs.conditionCount;
    }
    if (condAttrs.truthyIndexes !== undefined) {
      attributes[`mastra.${spanType}.truthy_indexes`] = JSON.stringify(condAttrs.truthyIndexes);
    }
    if (condAttrs.selectedSteps !== undefined) {
      attributes[`mastra.${spanType}.selected_steps`] = JSON.stringify(condAttrs.selectedSteps);
    }
    addEntryAttributes(attributes, spanType, condAttrs);
  }

  if (span.type === SpanType.WORKFLOW_CONDITIONAL_EVAL && span.attributes) {
    const evalAttrs = span.attributes as WorkflowConditionalEvalAttributes;
    if (evalAttrs.conditionIndex !== undefined) {
      attributes[`mastra.${spanType}.condition_index`] = evalAttrs.conditionIndex;
    }
    if (evalAttrs.result !== undefined) {
      attributes[`mastra.${spanType}.result`] = evalAttrs.result;
    }
  }

  if (span.type === SpanType.WORKFLOW_PARALLEL && span.attributes) {
    const parallelAttrs = span.attributes as WorkflowParallelAttributes;
    if (parallelAttrs.branchCount !== undefined) {
      attributes[`mastra.${spanType}.branch_count`] = parallelAttrs.branchCount;
    }
    if (parallelAttrs.parallelSteps !== undefined) {
      attributes[`mastra.${spanType}.parallel_steps`] = JSON.stringify(parallelAttrs.parallelSteps);
    }
    addEntryAttributes(attributes, spanType, parallelAttrs);
  }

  if (span.type === SpanType.WORKFLOW_LOOP && span.attributes) {
    const loopAttrs = span.attributes as WorkflowLoopAttributes;
    if (loopAttrs.loopType !== undefined) {
      attributes[`mastra.${spanType}.loop_type`] = loopAttrs.loopType;
    }
    if (loopAttrs.iteration !== undefined) {
      attributes[`mastra.${spanType}.iteration`] = loopAttrs.iteration;
    }
    if (loopAttrs.totalIterations !== undefined) {
      attributes[`mastra.${spanType}.total_iterations`] = loopAttrs.totalIterations;
    }
    if (loopAttrs.concurrency !== undefined) {
      attributes[`mastra.${spanType}.concurrency`] = loopAttrs.concurrency;
    }
    addEntryAttributes(attributes, spanType, loopAttrs);
  }

  if (span.type === SpanType.WORKFLOW_SLEEP && span.attributes) {
    const sleepAttrs = span.attributes as WorkflowSleepAttributes;
    if (sleepAttrs.durationMs !== undefined) {
      attributes[`mastra.${spanType}.duration_ms`] = sleepAttrs.durationMs;
    }
    if (sleepAttrs.untilDate !== undefined) {
      attributes[`mastra.${spanType}.until_date`] = sleepAttrs.untilDate.toISOString();
    }
    if (sleepAttrs.sleepType !== undefined) {
      attributes[`mastra.${spanType}.sleep_type`] = sleepAttrs.sleepType;
    }
    addEntryAttributes(attributes, spanType, sleepAttrs);
  }

  if (span.type === SpanType.WORKFLOW_WAIT_EVENT && span.attributes) {
    const waitAttrs = span.attributes as WorkflowWaitEventAttributes;
    if (waitAttrs.eventName !== undefined) {
      attributes[`mastra.${spanType}.event_name`] = waitAttrs.eventName;
    }
    if (waitAttrs.timeoutMs !== undefined) {
      attributes[`mastra.${spanType}.timeout_ms`] = waitAttrs.timeoutMs;
    }
    if (waitAttrs.eventReceived !== undefined) {
      attributes[`mastra.${spanType}.event_received`] = waitAttrs.eventReceived;
    }
    if (waitAttrs.waitDurationMs !== undefined) {
      attributes[`mastra.${spanType}.wait_duration_ms`] = waitAttrs.waitDurationMs;
    }
  }

  // Add error information if present
  if (span.errorInfo) {
    attributes[ATTR_ERROR_TYPE] = span.errorInfo.id || 'unknown';
    attributes[ATTR_ERROR_MESSAGE] = span.errorInfo.message;
    if (span.errorInfo.domain) {
      attributes['error.domain'] = span.errorInfo.domain;
    }
    if (span.errorInfo.category) {
      attributes['error.category'] = span.errorInfo.category;
    }
  }

  const threadId = span.metadata?.threadId;
  if (typeof threadId === 'string' && threadId.length > 0) {
    attributes[ATTR_GEN_AI_CONVERSATION_ID] = threadId;
  }

  return attributes;
}

/**
 * Canonical OTel provider keys mapped to a list of possible fuzzy aliases.
 */
const PROVIDER_ALIASES: Record<string, string[]> = {
  anthropic: ['anthropic', 'claude'],
  'aws.bedrock': ['awsbedrock', 'bedrock', 'amazonbedrock'],
  'azure.ai.inference': ['azureaiinference', 'azureinference'],
  'azure.ai.openai': ['azureaiopenai', 'azureopenai', 'msopenai', 'microsoftopenai'],
  cohere: ['cohere'],
  deepseek: ['deepseek'],
  'gcp.gemini': ['gcpgemini', 'gemini'],
  'gcp.gen_ai': ['gcpgenai', 'googlegenai', 'googleai'],
  'gcp.vertex_ai': ['gcpvertexai', 'vertexai'],
  groq: ['groq'],
  'ibm.watsonx.ai': ['ibmwatsonxai', 'watsonx', 'watsonxai'],
  mistral_ai: ['mistral', 'mistralai'],
  openai: ['openai', 'oai'],
  perplexity: ['perplexity', 'pplx'],
  x_ai: ['xai', 'x-ai', 'x_ai', 'x.com ai'],
};

/**
 * Normalize a provider input string into a matchable token.
 * Keep only alphanumerics and lowercase the result.
 */
function normalizeProviderString(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Attempts to map a providerName to one of the canonical OTel provider names.
 * If no match is found, returns the original providerName unchanged.
 */
function normalizeProvider(providerName: string): string {
  const normalized = normalizeProviderString(providerName);

  for (const [canonical, aliases] of Object.entries(PROVIDER_ALIASES)) {
    for (const alias of aliases) {
      if (normalized === alias) {
        return canonical;
      }
    }
  }

  // No match → return the raw input in lowercase
  return providerName.toLowerCase();
}
